import { SaveButton } from '@/components/_common';
import {
  useFinancialConfigurationProfile,
  useFinancialConfigurationUpdate,
} from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.hooks';
import { FinancialConfiguration } from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.types';
import useHasDiffValue from '@/hooks/hasDiffValue';
import { PageProps } from '@/types/PageProps';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Checkbox, Col, Form, Row, Skeleton, Typography } from 'antd';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

export default function FinancialConfigurationPage({ route }: PageProps) {
  const intl = useIntl();

  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const hasDiffValue = useHasDiffValue(formValues);

  const update = useFinancialConfigurationUpdate();
  const financialConfiguration = useFinancialConfigurationProfile();
  const data = flatInfiniteData(financialConfiguration.data);

  const handleUpdate = async () => {
    const values = await form.validateFields();
    const configurations = Object.entries(values).map((item) => ({
      column: item[0],
      active: item[1],
    })) as FinancialConfiguration[];

    await update.mutateAsync({ configurations });
  };

  return (
    <div className="page">
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page-header">
        <Typography.Title style={{ marginTop: 0 }} level={4}>
          Configurações do Financeiro
        </Typography.Title>

        <SaveButton
          type="primary"
          onClick={handleUpdate}
          loading={update.isLoading}
          disabled={!hasDiffValue}
        >
          <FormattedMessage id="commons.entity.save" />
        </SaveButton>
      </div>

      <Typography.Paragraph style={{ marginTop: '16px' }}>
        Os itens selecionados nesta tela serão exibidos como colunas nas páginas
        de{' '}
        <Link to="/app/financial/billToPay/releaseBillToPay" target="_blank">
          <strong>Contas a Pagar</strong>
        </Link>{' '}
        e{' '}
        <Link
          to="/app/financial/billToReceiver/releaseBillToReceiver"
          target="_blank"
        >
          <strong>Contas a Receber</strong>
        </Link>{' '}
        do sistema. Essa configuração será aplicada exclusivamente para o seu
        usuário.
      </Typography.Paragraph>

      <Skeleton active loading={financialConfiguration.isLoading}>
        <Form form={form} style={{ marginTop: '24px' }}>
          <Row gutter={[16, 12]}>
            {data.map((item) => (
              <Col span={24} lg={12} key={item.column}>
                <Form.Item
                  noStyle
                  name={item.column}
                  initialValue={item.active}
                  valuePropName="checked"
                >
                  <Checkbox>
                    <FormattedMessage
                      id={`settings.financialConfiguration.${item.column}`}
                    />
                  </Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      </Skeleton>
    </div>
  );
}
